import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Card, Badge, Table, Button } from "react-bootstrap";
import DataApi from "../../api/dataApi";
import ScrollToTop from "../common/ScrollToTop";

const PaymentDetail = ({ permissions }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);

  // Get data from navigation state (passed from Payment.js)
  const invoiceData = location.state?.invoice || {};
  const paymentsData = location.state?.payments || [];

  const formatCurrency = (val) => 
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0);

  const getStatusBadge = (status) => {
    let bg = "secondary";
    if (status === "PAID" || status === "Paid") bg = "success";
    else if (status === "PARTIAL" || status === "Partially Paid") bg = "warning";
    else bg = "danger";
    return bg;
  };

  // If we have data from navigation state, use it directly
  useEffect(() => {
    console.log("useEffect triggered:", { id, invoiceData, paymentsData });
    
    if (id) {
      // Always fetch from API when we have an ID (direct URL access or navigation)
      fetchData();
    } else if (invoiceData && Object.keys(invoiceData).length > 0) {
      // Only use navigation state if no ID is available
      setData(invoiceData);
      setPayments(paymentsData);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log("Fetching data for invoice ID:", id);
      
      // The URL param is invoice_id, so fetch invoice first
      const invoiceApi = new DataApi("invoice");
      const invoiceRes = await invoiceApi.fetchById(id);
      console.log("Invoice API response:", invoiceRes);
      
      if (invoiceRes?.data?.data) {
        setData(invoiceRes.data.data);
      } else if (invoiceRes?.data && !Array.isArray(invoiceRes.data)) {
        // Handle direct response
        setData(invoiceRes.data);
      }
      
      // Fetch payments for this invoice using the invoice endpoint
      const paymentApi = new DataApi("payment");
      const paymentRes = await paymentApi.get(`/invoice/${id}`);
      console.log("Payment API response:", paymentRes);
      console.log("Payment API response.data:", paymentRes?.data);
      
      // The endpoint returns array directly: res.json(payments)
      if (paymentRes?.data) {
        console.log("Payment data is array:", Array.isArray(paymentRes.data));
        if (Array.isArray(paymentRes.data)) {
          setPayments(paymentRes.data);
          console.log("Set payments to:", paymentRes.data);
        } else if (paymentRes.data?.data && Array.isArray(paymentRes.data.data)) {
          setPayments(paymentRes.data.data);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      console.error("Error response:", error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/payment");
  };

  if (loading) {
    return (
      <Container fluid>
        <ScrollToTop />
      </Container>
    );
  }

  if (!data) {
    return (
      <Container fluid>
        <ScrollToTop />
        <Card>
          <Card.Body className="text-center">
            <h4>No Data Found</h4>
            <p>Unable to load payment details.</p>
            <Button variant="primary" onClick={handleBack}>
              Back to List
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <>
      <Container fluid className="py-4" style={{ marginTop: "-20px" }}>
        <ScrollToTop />
        <Row className="justify-content-center">
          <Col lg={12} xl={12}>
            <Card className="border-0 shadow-sm detail-h4">
              <Card.Body>
                {/* Header */}
                <div
                  className="d-flex justify-content-between align-items-center mb-4 p-2"
                  style={{
                    color: "var(--primary-color)",
                    background: "var(--primary-background-color)",
                    borderRadius: "10px",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <button
                      onClick={handleBack}
                      className="shadow-sm d-flex align-items-center justify-content-center custom-btn-back"
                    >
                      <i className="fa-solid fa-arrow-left"></i>
                    </button>
                    <h5
                      className="fw-bold mb-0"
                      style={{ color: "var(--primary-color)" }}
                    >
                      <i className="fa-solid fa-credit-card me-2 fs-6"></i>
                      Payment Detail
                    </h5>
                  </div>
                </div>

                {/* Invoice Details Section */}
                <Row className="mt-4">
                  <Col md={12} className="mb-4">
                    <h6
                      className="fw-bold mb-0 d-flex align-items-center p-3 border rounded"
                      style={{
                        color: "var(--primary-color)",
                        background: "var(--primary-background-color)",
                        borderRadius: "10px",
                      }}
                    >
                      Invoice Information
                    </h6>
                  </Col>

                  <Row className="px-5">
                    <Col md={4}>
                      <div className="mb-3">
                        <label className="text-muted small fw-bold">Invoice Number</label>
                        <div className="fw-bold">{data.invoice_number || 'N/A'}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <label className="text-muted small fw-bold">Vendor Name</label>
                        <div className="fw-medium">{data.vendor_name || 'N/A'}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <label className="text-muted small fw-bold">Invoice Date</label>
                        <div className="fw-medium">
                          {data.invoice_date ? new Date(data.invoice_date).toLocaleDateString('en-GB') : 'N/A'}
                        </div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <label className="text-muted small fw-bold">Total Amount</label>
                        <div className="fw-bold">{formatCurrency(data.total_amount)}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <label className="text-muted small fw-bold">Paid Amount</label>
                        <div className="fw-bold text-success">{formatCurrency(data.paid_amount)}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <label className="text-muted small fw-bold">Due Amount</label>
                        <div className="fw-bold text-danger">{formatCurrency(data.due_amount)}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <label className="text-muted small fw-bold">Status</label>
                        <div>
                          <Badge bg={getStatusBadge(data.status)}>
                            {data.status || 'N/A'}
                          </Badge>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Row>

                {/* Payment History Section */}
                <Row className="mb-4">
                  <Col>
                    <Card
                      style={{
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    >
                      <Card.Body style={{ padding: 0 }}>
                        <div
                          style={{
                            padding: "20px 24px",
                            borderBottom: "1px solid #e9ecef",
                            background: "linear-gradient(to right, #f8f9fa, #ffffff)",
                          }}
                        >
                          <h6
                            style={{
                              margin: 0,
                              color: "var(--primary-color)",
                              fontSize: "16px",
                              fontWeight: "700",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <i className="fa-solid fa-history"></i>
                            Payment History
                          </h6>
                        </div>

                        <div style={{ padding: "24px" }}>
                          {payments && payments.length > 0 ? (
                            <div className="table-responsive">
                              <table
                                style={{
                                  width: "100%",
                                  fontSize: "14px",
                                  borderCollapse: "collapse",
                                }}
                              >
                                <thead>
                                  <tr
                                    style={{
                                      background: "#f8f9fa",
                                      borderBottom: "2px solid #e9ecef",
                                    }}
                                  >
                                    <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Date</th>
                                    <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Payment Mode</th>
                                    <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Transaction ID</th>
                                    <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Amount</th>
                                    <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#495057" }}>Remarks</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {payments.map((payment, index) => (
                                    <tr
                                      key={payment.id || index}
                                      style={{
                                        borderBottom: "1px solid #e9ecef",
                                        transition: "background-color 0.2s",
                                      }}
                                    >
                                      <td style={{ padding: "12px 16px", color: "#6c757d" }}>
                                        {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('en-GB') : 'N/A'}
                                      </td>
                                      <td style={{ padding: "12px 16px", color: "#6c757d" }}>
                                        <Badge bg="secondary">{payment.payment_mode || 'N/A'}</Badge>
                                      </td>
                                      <td style={{ padding: "12px 16px", color: "#6c757d", fontFamily: "monospace" }}>
                                        {payment.transaction_id || '—'}
                                      </td>
                                      <td style={{ padding: "12px 16px", color: "#6c757d", fontWeight: "600", color: "var(--primary-color)" }}>
                                        {formatCurrency(payment.amount_paid)}
                                      </td>
                                      <td style={{ padding: "12px 16px", color: "#6c757d" }}>
                                        {payment.remarks || '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div
                              style={{
                                padding: "32px 16px",
                                textAlign: "center",
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                border: "1px dashed #dee2e6",
                              }}
                            >
                              <i
                                className="fa-solid fa-inbox"
                                style={{
                                  fontSize: "32px",
                                  color: "#adb5bd",
                                  marginBottom: "8px",
                                  display: "block",
                                }}
                              ></i>
                              <p
                                style={{
                                  color: "#6c757d",
                                  margin: 0,
                                  fontSize: "14px",
                                }}
                              >
                                No payment history available.
                              </p>
                            </div>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PaymentDetail;

